import { Box, Form, Layout, Text } from '@shopify/polaris'
import { Formik } from 'formik'
import { useCallback, useState } from 'react'
import { mutate } from 'swr'

import { Result } from '../common/api'
import { ResultToastOrBanner, ShowIntercomLink } from '../common/helpers'
import { FormikSaveBar } from '../main/SaveBarProvider'
import { CustomerPortalSettings, patchCustomerPortalSettings } from './api'
import { CancellationFlowCard } from './CancellationFlowCard'
import { CustomerPortalCard } from './CustomerPortalCard'
import { CustomerPortalThemeCard } from './CustomerPortalThemeCard'
import { NotificationsCard } from './NotificationsCard'
interface Props {
  initialValues: CustomerPortalSettings
}

const LayoutAnnotatedSection = ({
  title,
  description,
  children,
}: {
  title: string
  description: React.ReactNode
  children: React.ReactNode
}) => {
  const titleWithPadding = (
    <Box paddingInline={{ xs: '400', sm: '0', md: '0', lg: '0', xl: '0' }}>{title}</Box>
  )
  const descriptionWithPadding = (
    <Box paddingInline={{ xs: '400', sm: '0', md: '0', lg: '0', xl: '0' }}>{description}</Box>
  )
  return (
    <Layout.AnnotatedSection title={titleWithPadding} description={descriptionWithPadding}>
      {children}
    </Layout.AnnotatedSection>
  )
}

export const CustomerPortalSettingsForm = ({ initialValues }: Props) => {
  const [saveResult, setSaveResult] = useState<Result<any> | null>(null)

  const handleSubmit = useCallback(async (settings: CustomerPortalSettings, actions: any) => {
    actions.setSubmitting(true)
    const res = await patchCustomerPortalSettings(settings)
    actions.setSubmitting(false)

    setSaveResult(res)

    if (res.status === 'success' && res.data) {
      mutate(['customer-portal-settings'], res.data)
    } else {
      window.scrollTo(0, 0)
    }
  }, [])

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
      {(formik) => (
        <>
          <Form onSubmit={formik.handleSubmit}>
            <ResultToastOrBanner result={saveResult} setResult={setSaveResult} />
            <Layout>
              <LayoutAnnotatedSection
                title="Installation"
                description={
                  <Text as="p">
                    Install and customize the look and feel of the customer portal.{' '}
                    <ShowIntercomLink message="I have questions about the Customer Portal settings.">
                      Chat with us
                    </ShowIntercomLink>
                    .
                  </Text>
                }
              >
                <CustomerPortalThemeCard />
              </LayoutAnnotatedSection>
              <LayoutAnnotatedSection
                title="Customer Actions"
                description={
                  <Text as="p">
                    Define the options available to customers in the self-serve portal. Questions?{' '}
                    <ShowIntercomLink message="I want to discuss the Customer Portal Actions section.">
                      Let's chat
                    </ShowIntercomLink>
                    .
                  </Text>
                }
              >
                <CustomerPortalCard />
              </LayoutAnnotatedSection>
              <LayoutAnnotatedSection
                title="Cancellation Flow"
                description={
                  <Text as="p">
                    Manage the subscription cancellation process.{' '}
                    <ShowIntercomLink message="I need assistance with the Cancellation Flow section.">
                      Chat with us
                    </ShowIntercomLink>
                    .
                  </Text>
                }
              >
                <CancellationFlowCard />
              </LayoutAnnotatedSection>
              <LayoutAnnotatedSection
                title="Customer Notifications"
                description={
                  <Text as="p">
                    Configure email notifications for your customers. Click the title to edit.
                    Questions?{' '}
                    <ShowIntercomLink message="I have questions about the Customer Notifications section.">
                      Let's chat
                    </ShowIntercomLink>
                    .
                  </Text>
                }
              >
                <NotificationsCard />
              </LayoutAnnotatedSection>
            </Layout>
          </Form>
          <FormikSaveBar />
        </>
      )}
    </Formik>
  )
}
